const TRANSPARENT_COLOR = 'rgba(0,0,0,0)';

const insetBuilder = (base, isNative) => {
  if (isNative) {
    return {
      insetTop: base / 2,
      insetRight: base,
      insetBottom: base / 2,
      insetLeft: base
    };
  }
  return {
    insetTop: `${base / 2}rem`,
    insetRight: `${base}rem`,
    insetBottom: `${base / 2}rem`,
    insetLeft: `${base}rem`
  };
};

/**
 * Map tokens to css-friendly properties. and split into
 * groups (`theme`, `isPill`, `text`, `size`) so they can be
 * conditionally applied depending on their namesake props
 *
 * @param {{}} tokens system tokens. At least theme, font, border, and space
 */
export const mapTokensToProps = (theme, isNative = false) => ({
  theme: {
    ...tokenizeAppearanceAndThemeProps(theme)
  },
  borderRadius: {
    start: [theme.size.border.radius.twoX, 0, 0, theme.size.border.radius.twoX],
    default: 0,
    end: [0, theme.size.border.radius.twoX, theme.size.border.radius.twoX, 0]
  },
  text: {
    fontFamily: theme.font.base.family,
    fontStyle: 'normal',
    letterSpacing: 0,
    fontWeight: `${theme.font.base.weight.regular}` // RN requires string weight, web doesn't care
  },
  size: {
    s: {
      fontSize: theme.font.base.size.s,
      iconSize: 's', // uses tokens within Icon
      minWidth: 96, // no token for this
      lineHeight: 1, // no token for this
      ...insetBuilder(isNative ? 16 : 1, isNative)
    },
    m: {
      fontSize: theme.font.base.size.m,
      iconSize: 'm', // uses tokens within Icon
      minWidth: 112, // no token for this
      lineHeight: 1.125,
      ...insetBuilder(isNative ? 16 : 1, isNative)
    },
    l: {
      fontSize: theme.font.base.size.m,
      iconSize: 'm', // uses tokens within Icon
      minWidth: 128, // no token for this
      lineHeight: 1.125,
      ...insetBuilder(isNative ? 24 : 1.5, isNative)
    }
  },
  iconOnly: {
    s: {
      fontSize: '1.5rem',
      iconSize: 'l', // uses tokens within Icon
      minWidth: 0, // no tokens for these
      insetTop: 3,
      insetRight: 3,
      insetBottom: 3,
      insetLeft: 3
    },
    m: {
      fontSize: '1.5rem',
      iconSize: 'l', // uses tokens within Icon
      minWidth: 0, // no tokens for these
      insetTop: 5,
      insetRight: 5,
      insetBottom: 5,
      insetLeft: 5
    },
    l: {
      fontSize: '1.5rem',
      iconSize: 'l', // uses tokens within Icon
      minWidth: 0, // no tokens for these
      insetTop: 9,
      insetRight: 9,
      insetBottom: 9,
      insetLeft: 9
    }
  },
  border: {
    default: theme.size.border.base,
    focus: theme.size.border.focus
  }
});

/**
 * First by theme, then by appearance, create a mapping of tokens to css properties to use
 * as inline styles on web or mobile. Requires tokens have matching keys defined for each
 * theme.
 *
 * @param {{ [themeName]: any }} theme the top-level `theme` object in design system tokens
 * @returns {{}} {themeNames > appearanceNames > css properties }
 */
function tokenizeAppearanceAndThemeProps(theme) {
  return {
    initial: {
      backgroundColor: theme.backgroundColor.base,
      borderColor: theme.borderColor.interactive.base,
      textColor: theme.font.color.interactive.base,
      outlineColor: TRANSPARENT_COLOR,
      zIndex: 0 // default to source order
    },
    hover: {
      backgroundColor: theme.backgroundColor.interactive.hover,
      borderColor: theme.borderColor.interactive.hover,
      textColor: theme.font.color.interactive.hover
    },
    active: {
      backgroundColor: theme.backgroundColor.interactive.fill.base,
      borderColor: theme.borderColor.interactive.base,
      textColor: theme.font.color.interactive.fill
    },
    disabled: {
      backgroundColor: theme.backgroundColor.disabled,
      // using the same background color token creates a
      // layering issue due to alpha color value
      borderColor: TRANSPARENT_COLOR,
      textColor: theme.font.color.interactive.disabled
    },
    focus: {
      outlineColor: theme.backgroundColor.interactive.fill.focus,
      zIndex: 1 // display focus ring over adjacent buttons
    }
  };
}
